import { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SortIcon from '@mui/icons-material/Sort';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import {
  getTableV2DetailBodyCellSx,
  getTableV2HeaderCellSx,
  tableV2DetailPanelRowSx,
  tableV2DetailPanelWrapperSx,
  tableV2TableSx,
} from '../../../components/v2/tableV2Styles';
import { fraseCurta, labelSeveridade, significadoCompleto, sortPessoas } from './constants';
import LegendaClassificacaoDialog from './LegendaClassificacaoDialog';

const DETAIL_ROW_HEIGHT = 44;
const CHIPS_POR_LINHA = 3;

const linhasDeChips = (itens) => {
  const linhas = [];
  for (let i = 0; i < itens.length; i += CHIPS_POR_LINHA) {
    linhas.push(itens.slice(i, i + CHIPS_POR_LINHA));
  }
  return linhas;
};

const DIAS_AJUDA = 'Os dias da pessoa são os dias distintos com alguma ocorrência. A soma das classificações pode ser maior, porque o mesmo dia entra em mais de um tipo.';

const SORT_OPTIONS = [
  { mode: 'SEV_PIOR', title: 'Severidade · pior primeiro', field: 'severidade', direction: 'asc' },
  { mode: 'SEV_MENOR', title: 'Severidade · menor primeiro', field: 'severidade', direction: 'desc' },
  { mode: 'NOME_ASC', title: 'Usuário A–Z', field: 'nome', direction: 'asc' },
  { mode: 'NOME_DESC', title: 'Usuário Z–A', field: 'nome', direction: 'desc' },
  { mode: 'REGIME_ASC', title: 'Regime A–Z', field: 'regime', direction: 'asc' },
  { mode: 'REGIME_DESC', title: 'Regime Z–A', field: 'regime', direction: 'desc' },
  { mode: 'DIAS_DESC', title: 'Dias com ocorrência ↓', field: 'dias', direction: 'desc' },
  { mode: 'DIAS_ASC', title: 'Dias com ocorrência ↑', field: 'dias', direction: 'asc' },
];

const corSeveridade = (severidade) => {
  if (severidade === 'ALTA') return 'error';
  if (severidade === 'MEDIA') return 'warning';
  return 'default';
};

const ResumoTable = ({ pessoas, diaSelecionado, onOpenResumo, onOpenCalendario }) => {
  const [sortMode, setSortMode] = useState('NOME_ASC');
  const [anchorEl, setAnchorEl] = useState(null);
  const [aberta, setAberta] = useState(null);
  const [legendaAberta, setLegendaAberta] = useState(false);
  const sort = SORT_OPTIONS.find((option) => option.mode === sortMode) || SORT_OPTIONS[0];
  const sorted = useMemo(
    () => sortPessoas(pessoas, sort.field, sort.direction),
    [pessoas, sort.field, sort.direction],
  );

  if (!pessoas.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {diaSelecionado
          ? 'Nenhuma inconsistência neste dia.'
          : 'Nenhuma inconsistência neste ciclo.'}
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={{ px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}
      >
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<InfoOutlinedIcon />}
          onClick={() => setLegendaAberta(true)}
        >
          Significado
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<SortIcon />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          Ordenar
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          {SORT_OPTIONS.map((option) => (
            <MenuItem
              key={option.mode}
              selected={sortMode === option.mode}
              onClick={() => {
                setSortMode(option.mode);
                setAnchorEl(null);
              }}
            >
              {option.title}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Usuário</TableCell>
            <TableCell>Regime</TableCell>
            <TableCell align="right">
              <Tooltip title={DIAS_AJUDA}>
                <span>Dias</span>
              </Tooltip>
            </TableCell>
            <TableCell>Severidade</TableCell>
            <TableCell>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <span>Classificações</span>
                <Tooltip title={DIAS_AJUDA}>
                  <IconButton size="small" aria-label="Como ler os dias das classificações" sx={{ p: 0.25 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((pessoa) => {
            const key = pessoa.usuarioId || pessoa.usuarioNome;
            const abertaEsta = aberta === key;
            return (
              <Fragment key={key}>
                <TableRow
                  hover
                  onClick={() => setAberta(abertaEsta ? null : key)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <IconButton
                      size="small"
                      aria-label={abertaEsta ? 'Fechar classificações' : 'Abrir classificações'}
                    >
                      {abertaEsta ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{pessoa.usuarioNome}</Typography>
                  </TableCell>
                  <TableCell>{pessoa.regime}</TableCell>
                  <TableCell align="right">{pessoa.diasComOcorrencia}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={labelSeveridade(pessoa.piorSeveridade) || '—'}
                      color={corSeveridade(pessoa.piorSeveridade)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {linhasDeChips(pessoa.classificacoes).map((linha) => (
                        <Stack
                          key={linha.map((parte) => parte.codigo).join('|')}
                          direction="row"
                          spacing={0.5}
                          useFlexGap
                        >
                          {linha.map((parte) => (
                            <Tooltip
                              key={`${parte.codigo}-${parte.severidade || 'sem'}`}
                              title={significadoCompleto(parte.codigo)}
                            >
                              <Chip
                                size="small"
                                label={`${parte.tipo} · ${parte.dias}`}
                                color={corSeveridade(parte.severidade)}
                                variant="outlined"
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<SummarizeOutlinedIcon />}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenResumo(pessoa);
                        }}
                      >
                        Resumo
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<CalendarMonthOutlinedIcon />}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenCalendario(pessoa);
                        }}
                      >
                        Calendário
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
                {abertaEsta && (
                  <TableRow hover={false}>
                    <TableCell colSpan={7} sx={tableV2DetailPanelRowSx}>
                      <Box sx={tableV2DetailPanelWrapperSx}>
                        <Box sx={{ overflowX: 'auto', px: { xs: 0.5, sm: 1 }, py: 0.5 }}>
                          <Table size="medium" aria-label="Classificações da pessoa" sx={tableV2TableSx}>
                            <TableHead>
                              <TableRow hover={false}>
                                <TableCell sx={getTableV2HeaderCellSx(DETAIL_ROW_HEIGHT)}>Classificação</TableCell>
                                <TableCell sx={getTableV2HeaderCellSx(DETAIL_ROW_HEIGHT)}>Severidade</TableCell>
                                <TableCell sx={{ ...getTableV2HeaderCellSx(DETAIL_ROW_HEIGHT), textAlign: 'right' }}>
                                  Dias
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {pessoa.classificacoes.map((parte, rowIndex) => {
                                const cellSx = {
                                  ...getTableV2DetailBodyCellSx(DETAIL_ROW_HEIGHT, rowIndex),
                                  height: 'auto',
                                  minHeight: DETAIL_ROW_HEIGHT,
                                  maxHeight: 'none',
                                  whiteSpace: 'normal',
                                  overflow: 'visible',
                                  py: 1,
                                };
                                return (
                                  <TableRow
                                    key={`${parte.codigo}-${parte.severidade || 'sem'}`}
                                    hover
                                    onClick={() => onOpenResumo(pessoa, parte)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <TableCell sx={cellSx}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {parte.tipo}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                        {fraseCurta(parte.codigo)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                      <Chip
                                        size="small"
                                        label={labelSeveridade(parte.severidade) || '—'}
                                        color={corSeveridade(parte.severidade)}
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{parte.dias}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      <LegendaClassificacaoDialog open={legendaAberta} onClose={() => setLegendaAberta(false)} />
    </TableContainer>
  );
};

ResumoTable.propTypes = {
  pessoas: PropTypes.array.isRequired,
  diaSelecionado: PropTypes.string,
  onOpenResumo: PropTypes.func.isRequired,
  onOpenCalendario: PropTypes.func.isRequired,
};

export default ResumoTable;
